import axiosClient from '../axiosClient';

// Validation Rule Types (matches backend enum)
// Backend sender camelCase i JSON, men vi normaliserer til PascalCase internt
export type ValidationRuleType =
  | 'Required'
  | 'AllowedValues'
  | 'MinValue'
  | 'MaxValue'
  | 'MinLength'
  | 'MaxLength'
  | 'Regex'
  | 'DateBefore'
  | 'DateAfter'
  | 'Unique'
  | 'PhoneNumber'
  | 'Ean13'
  | 'Ean15'
  | 'VatNumber'
  | 'ConditionalRequired'
  | 'AtLeastOneRequired'
  | 'ConditionalAllowedValues'
  | 'GroupExists'
  | 'SequenceReferenceExists';

export type ValidationSeverity = 'Error' | 'Warning';

// ValidationRuleParameters interface (matches backend)
export interface ValidationRuleParameters {
  values?: string[];
  minNumericValue?: number;
  maxNumericValue?: number;
  minDateValue?: string; // ISO date string
  maxDateValue?: string; // ISO date string
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  description?: string;
  compareColumn?: string;
  conditionColumn?: string;
  conditionOperator?: string;
  conditionValue?: string;
  additionalColumns?: string[];
  columns?: string[];
  allowedValuesWhenConditionTrue?: string[];
  groupByColumn?: string;
  // SequenceReferenceExists (US 2665): måltabellens tableKey + målkolonnens key
  targetTableKey?: string;
  targetColumnKey?: string;
}

// Response type (matches backend ValidationRuleResponse)
export interface ValidationRuleResponse {
  validationRuleId: number;
  projectId: string; // GUID
  columnName: string;
  ruleType: ValidationRuleType;
  parameters: ValidationRuleParameters | null;
  severity: ValidationSeverity;
  isActive: boolean;
  createdAt: string;
  createdBy: string | null;
}

// Request types (matches backend)
export interface CreateValidationRuleRequest {
  columnName: string;
  ruleType: ValidationRuleType;
  parameters?: ValidationRuleParameters | null;
  severity?: ValidationSeverity;
}

export interface UpdateValidationRuleRequest {
  columnName: string;
  parameters: ValidationRuleParameters | null;
  severity: ValidationSeverity;
  isActive: boolean;
}

// ============== ValidationRule CRUD ==============

/**
 * Henter alle valideringsregler for en tabell
 */
export async function getValidationBindingsByTable (
  tableKey: string
): Promise<ValidationRuleResponse[]> {
  const tableRef = String(tableKey ?? '').trim();
  const response = await axiosClient.get(`v2/validation/tables/${tableRef}/bindings`);
  return response.data;
}

/**
 * Oppretter en ny valideringsregel for en tabell
 */
export async function createValidationBinding (
  tableKey: string,
  request: CreateValidationRuleRequest
): Promise<ValidationRuleResponse> {
  const tableRef = String(tableKey ?? '').trim();
  const response = await axiosClient.post(
    `v2/validation/tables/${tableRef}/bindings`,
    request
  );
  return response.data;
}

/**
 * Oppdaterer en valideringsregel
 */
export async function updateValidationRule (
  ruleId: number,
  request: UpdateValidationRuleRequest
): Promise<ValidationRuleResponse> {
  const response = await axiosClient.put(`v2/validation/rules/${ruleId}`, request);
  return response.data;
}

/**
 * Sletter en valideringsregel
 */
export async function deleteValidationBinding (ruleId: number): Promise<void> {
  await axiosClient.delete(`v2/validation/rules/${ruleId}`);
}

// ============== Hjelpefunksjoner ==============

/**
 * Henter kolonner som har valideringsregler for en tabell
 * Returnerer et Set med kolonnenavn for rask oppslag
 */
export async function getColumnsWithValidationRules (
  tableKey: string
): Promise<Set<string>> {
  const rules = await getValidationBindingsByTable(tableKey);
  const columns = new Set<string>();

  for (const rule of rules) {
    if (rule.isActive) {
      columns.add(rule.columnName);
      // Legg til additionalColumns for Unique-regler
      if (rule.ruleType === 'Unique' && rule.parameters?.additionalColumns) {
        for (const col of rule.parameters.additionalColumns) {
          columns.add(col);
        }
      }
    }
  }

  return columns;
}

export async function getColumnsWithValidationRulesByTableKey (
  tableKey: string
): Promise<Set<string>> {
  const normalizedKey = String(tableKey || '').trim();
  if (!normalizedKey) {
    return new Set<string>();
  }
  return getColumnsWithValidationRules(normalizedKey);
}

/**
 * Mapper regel-type til norsk label
 */
export function getRuleTypeLabel (ruleType: ValidationRuleType): string {
  const labels: Record<ValidationRuleType, string> = {
    Required: 'Påkrevd',
    AllowedValues: 'Gyldige verdier',
    MinValue: 'Min verdi',
    MaxValue: 'Max verdi',
    MinLength: 'Min lengde',
    MaxLength: 'Max lengde',
    Regex: 'Regex-mønster',
    DateBefore: 'Dato før',
    DateAfter: 'Dato etter',
    Unique: 'Unik verdi',
    PhoneNumber: 'Telefonnummer',
    Ean13: 'EAN-13 (GTIN)',
    Ean15: 'EAN-15 (GTIN)',
    VatNumber: 'Organisasjonsnummer',
    ConditionalRequired: 'Betinget påkrevd',
    AtLeastOneRequired: 'Minst én påkrevd',
    ConditionalAllowedValues: 'Betingede tillatte verdier',
    GroupExists: 'Gruppe-eksistens',
    SequenceReferenceExists: 'Løpenummer-referanse'
  };
  return labels[ruleType] || ruleType;
}

// Dispatch table: each rule type maps to a pure description builder. Replaces a
// large switch to cut cognitive complexity without changing behavior. A Map (not a
// plain object) avoids prototype-key hits, so unknown types fall through to the same
// fallback the switch default produced.
type RuleDescriptionHandler = (
  params: ValidationRuleParameters,
  columnName: string,
  columnStr: string
) => string;

const ruleDescriptionHandlers = new Map<ValidationRuleType, RuleDescriptionHandler>([
  ['Required', (params, columnName, columnStr) => `${columnStr} er påkrevd`],
  ['AllowedValues', (params, columnName, columnStr) => {
    const values = Array.isArray(params.values) ? params.values : [];
    return `${columnStr} må være en av: ${values.join(', ') || '(ingen verdier)'}`;
  }],
  ['MinValue', (params, columnName, columnStr) => {
    const minNum = params.minNumericValue;
    const minDate = params.minDateValue;
    if (minNum != null) {
      return `${columnStr} må være minst ${minNum}`;
    }
    if (minDate != null) {
      return `${columnStr} må være minst ${minDate}`;
    }
    return `${columnStr} må være minst (verdi ikke satt)`;
  }],
  ['MaxValue', (params, columnName, columnStr) => {
    const maxNum = params.maxNumericValue;
    const maxDate = params.maxDateValue;
    if (maxNum != null) {
      return `${columnStr} må være maksimalt ${maxNum}`;
    }
    if (maxDate != null) {
      return `${columnStr} må være maksimalt ${maxDate}`;
    }
    return `${columnStr} må være maksimalt (verdi ikke satt)`;
  }],
  ['MinLength', (params, columnName, columnStr) => `${columnStr} må ha minst ${params.minLength} tegn`],
  ['MaxLength', (params, columnName, columnStr) => `${columnStr} må ha maksimalt ${params.maxLength} tegn`],
  ['Regex', (params, columnName, columnStr) => `${columnStr} må matche mønster: ${params.description || params.pattern}`],
  ['DateBefore', (params, columnName, columnStr) => `${columnStr} må være før ${params.compareColumn || '(ukjent kolonne)'}`],
  ['DateAfter', (params, columnName, columnStr) => `${columnStr} må være etter ${params.compareColumn || '(ukjent kolonne)'}`],
  ['Unique', (params, columnName, columnStr) => {
    const additionalCols = params.additionalColumns || [];
    const allColumns = [columnName, ...additionalCols];
    return allColumns.length > 1
      ? `Kombinasjonen av ${allColumns.join(', ')} må være unik`
      : `${columnStr} må ha unike verdier`;
  }],
  ['PhoneNumber', (params, columnName, columnStr) => `${columnStr} må være et gyldig telefonnummer`],
  ['Ean13', (params, columnName, columnStr) => `${columnStr} må være et gyldig EAN-13 (GTIN)`],
  ['Ean15', (params, columnName, columnStr) => `${columnStr} må være et gyldig EAN-15 (GTIN)`],
  ['VatNumber', (params, columnName, columnStr) => `${columnStr} må være et gyldig organisasjonsnummer`],
  ['ConditionalRequired', (params, columnName, columnStr) => {
    return `${columnStr} er påkrevd når ${params.conditionColumn || '(ukjent)'} ${params.conditionOperator || ''} ${params.conditionValue || ''}`;
  }],
  ['AtLeastOneRequired', (params, columnName, columnStr) => {
    const cols = Array.isArray(params.columns) ? params.columns : [];
    return cols.length > 0
      ? `Minst én av ${cols.join(', ')} må være utfylt`
      : `Minst én kolonne må være utfylt (ingen kolonner valgt)`;
  }],
  ['ConditionalAllowedValues', (params, columnName, columnStr) => {
    const allowed = Array.isArray(params.allowedValuesWhenConditionTrue)
      ? params.allowedValuesWhenConditionTrue
      : [];
    const op = params.conditionOperator || '';
    const condCol = params.conditionColumn || '(ukjent)';
    const condVal = params.conditionValue || '';
    let predicate: string;
    if (op === 'Empty') {
      predicate = `${condCol} er tom`;
    } else if (op === 'NotEmpty') {
      predicate = `${condCol} har verdi`;
    } else {
      predicate = `${condCol} ${op} ${condVal}`;
    }
    return allowed.length > 0
      ? `Når ${predicate} må ${columnStr} være en av: ${allowed.join(', ')}`
      : `Når ${predicate} må ${columnStr} ha verdi (ingen tillatte verdier valgt)`;
  }],
  ['GroupExists', (params, columnName, columnStr) => {
    const op = params.conditionOperator || '';
    const condCol = params.conditionColumn || '(ukjent)';
    const condVal = params.conditionValue || '';
    const groupCol = params.groupByColumn || columnStr;
    let predicate: string;
    if (op === 'Empty') {
      predicate = `${condCol} er tom`;
    } else if (op === 'NotEmpty') {
      predicate = `${condCol} har verdi`;
    } else {
      predicate = `${condCol} ${op} ${condVal}`;
    }
    return `Per ${groupCol} må minst én rad oppfylle: ${predicate}`;
  }],
  ['SequenceReferenceExists', (params, columnName, columnStr) => {
    // US 2665: regelen opprettes/slettes av systemet med kolonnetypen «Løpenummer-referanse»;
    // her vises målet lesbart (alvorlighet flippes som for andre regler).
    const targetTable = params.targetTableKey || '(ukjent tabell)';
    const targetColumn = params.targetColumnKey || '(ukjent kolonne)';
    return `Verdien i ${columnStr} må finnes blant løpenumrene i måltabellen (${targetTable}.${targetColumn})`;
  }]
]);

/**
 * Genererer lesbar beskrivelse fra parameters objekt
 */
export function getRuleDescription (
  ruleType: ValidationRuleType,
  parameters: ValidationRuleParameters | null,
  columnName: string
): string {
  const columnStr = columnName;

  try {
    const params = parameters || {};
    const handler = ruleDescriptionHandlers.get(ruleType);
    return handler
      ? handler(params, columnName, columnStr)
      : `${columnStr}: ${ruleType}`;
  } catch {
    return `${columnStr}: ${ruleType}`;
  }
}
